import {useUpmPlant} from '@hconnect/upm'
import {Button, Tooltip} from '@hm/fundamental'
import {mdiDeleteOutline, mdiPencil} from '@mdi/js'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {ShutdownStatus} from '@refra/modules/common/enums'
import {useConfirmDialog} from '@refra/modules/common/hooks/useConfirmDialog'
import {useShutdowns} from '@refra/modules/common/providers'
import {StaticPartShutdownPrintButton} from '@refra/modules/refra-static-parts/components/StaticPartShutdownPrint'
import {useRemoveStaticPartReplacement} from '@refra/modules/refra-static-parts/hooks/mutations/useRemoveStaticPartReplacement'
import {StaticPartComponentDto} from '@refra/modules/refra-static-parts/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface ShutdownDetailInfoActionsProps {
  selectedComponent: StaticPartComponentDto
  replacementId?: number
}

export const ShutdownDetailInfoActions = ({
  selectedComponent,
  replacementId
}: ShutdownDetailInfoActionsProps) => {
  const navigate = useNavigate()
  const {selectedShutdown} = useShutdowns()
  const {t} = useTranslation()
  const {mutate: removeReplacement} = useRemoveStaticPartReplacement()
  const {openDialog} = useConfirmDialog()
  const plantCode = useUrlParam('plantCode')
  const selectedKilnId = useUrlParam('selectedKilnId')
  const upmPlant = useUpmPlant({plantId: plantCode})

  const isShutdownEditable = selectedShutdown?.shutdownStatus === ShutdownStatus.Draft

  const openConfirmDialog = () => {
    if (replacementId && selectedShutdown) {
      openDialog({
        title: t('staticParts.replacementDetail.remove.dialogTitle'),
        description: t('staticParts.replacementDetail.remove.dialogDescription'),
        mainAction: {
          text: t('common.delete'),
          icon: mdiDeleteOutline,
          variant: 'negative',
          onAction: () =>
            removeReplacement({
              plantUpmId: upmPlant.upmId,
              kilnUpmId: selectedKilnId,
              shutdownId: selectedShutdown?.id,
              replacementId
            })
        }
      })
    }
  }

  return (
    <div className="flex gap-4" data-cy="static_part_shutdown_detail_info_actions">
      {replacementId && <StaticPartShutdownPrintButton selectedComponent={selectedComponent} />}
      {replacementId && (
        <Tooltip
          content={t(
            isShutdownEditable ? 'common.delete' : 'staticParts.replacementDetail.shutdownTooltip'
          )}
        >
          <Button
            disabled={!(isShutdownEditable && selectedComponent)}
            onClick={openConfirmDialog}
            variant="negative"
            icon={mdiDeleteOutline}
          >
            {t('common.delete')}
          </Button>
        </Tooltip>
      )}
      {selectedShutdown && (
        <Tooltip
          content={t(
            isShutdownEditable ? 'common.edit' : 'staticParts.replacementDetail.shutdownTooltip'
          )}
        >
          <Button
            disabled={!(isShutdownEditable && selectedComponent)}
            onClick={() => navigate(`edit/${selectedComponent.id}/${selectedShutdown.id}`)}
            variant="secondary"
            icon={mdiPencil}
          >
            {t('common.edit')}
          </Button>
        </Tooltip>
      )}
    </div>
  )
}
